import { useState, useRef, useEffect, useCallback } from 'react';

const THRESHOLD = 70; // px pull distance to trigger refresh

/* ── Animated arrow / spinner icon ───────────────────────────────────────── */
const Indicator = ({ refreshing, progress }) => (
  <div
    className="w-9 h-9 rounded-full flex items-center justify-center card-shadow"
    style={{ background: '#fff', border: '1px solid #e2e8f0' }}
  >
    {refreshing ? (
      <svg
        className="animate-spin"
        width="18" height="18" viewBox="0 0 24 24"
        fill="none" stroke="#6366f1" strokeWidth="2.5"
        strokeLinecap="round"
      >
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
      </svg>
    ) : (
      <svg
        width="16" height="16" viewBox="0 0 24 24"
        fill="none" stroke="#6366f1" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: `rotate(${Math.round(progress * 180)}deg)`, transition: 'transform 0.1s linear' }}
      >
        <path d="M12 5v14M5 12l7 7 7-7" />
      </svg>
    )}
  </div>
);

/* ── Component ────────────────────────────────────────────────────────────── */
const PullToRefresh = ({ onRefresh, children }) => {
  const scrollRef   = useRef(null);
  const startYRef   = useRef(0);
  const pullingRef  = useRef(false);
  const refreshingRef = useRef(false);

  const [pullDist,   setPullDist]   = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const triggerRefresh = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    setPullDist(0);
    try { await onRefresh(); } finally {
      setRefreshing(false);
      refreshingRef.current = false;
    }
  }, [onRefresh]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onStart = (e) => {
      if (el.scrollTop === 0 && !refreshingRef.current) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = true;
      }
    };

    const onMove = (e) => {
      if (!pullingRef.current || refreshingRef.current) return;
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta > 0) {
        e.preventDefault();
        setPullDist(Math.min(delta * 0.5, THRESHOLD * 1.2));
      } else {
        pullingRef.current = false;
        setPullDist(0);
      }
    };

    const onEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      // read current pullDist via a one-off setState callback to avoid stale closure
      setPullDist((prev) => {
        if (prev >= THRESHOLD) { triggerRefresh(); return 0; }
        return 0;
      });
    };

    el.addEventListener('touchstart', onStart, { passive: true  });
    el.addEventListener('touchmove',  onMove,  { passive: false });
    el.addEventListener('touchend',   onEnd,   { passive: true  });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove',  onMove);
      el.removeEventListener('touchend',   onEnd);
    };
  }, [triggerRefresh]);

  const indicatorHeight = refreshing ? 56 : pullDist;
  const progress        = Math.min(pullDist / THRESHOLD, 1);

  return (
    <div ref={scrollRef} className="page-scroll">
      {/* Pull / refresh indicator */}
      <div
        style={{
          height: indicatorHeight,
          transition: (!pullingRef.current || refreshing) ? 'height 0.3s ease' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {(pullDist > 4 || refreshing) && (
          <div style={{ opacity: refreshing ? 1 : progress, transform: `scale(${0.6 + 0.4 * progress})` }}>
            <Indicator refreshing={refreshing} progress={progress} />
          </div>
        )}
      </div>

      {children}
    </div>
  );
};

export default PullToRefresh;
